import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Circle,
  CheckCircle,
  Filter,
  Grid3x3,
  Inbox,
  Layers,
  Shuffle,
  Tag,
  Zap,
} from "lucide-react";
import { Badge } from "@/components/Badge";
import { EmptyState } from "@/components/EmptyState";
import { ProductPricingSummary } from "@/components/ProductPricingSummary";
import {
  generateId,
  useApp,
  type Card,
  type ProductType,
} from "@/context/AppContext";
import { generateBundles } from "@/features/bundleEngine";
import { generateTemplateBundles } from "@/features/bundleTemplateEngine";
import { generateProductOpportunities } from "@/features/productOpportunityEngine";
import { buildDescriptionForProduct } from "@/features/bundleDescription";
import {
  RARITY_DISPLAY_ORDER,
  summarizeRarityCounts,
} from "@/features/rarityComposition";
import type { ProductOpportunity } from "@/types/productOpportunity";
import { type StudioCategory } from "@/types/bundleTemplate";

export const Route = createFileRoute("/products/studio")({
  component: ProductStudioPage,
});

type InventorySource =
  | { kind: "all-unlisted" }
  | { kind: "lot"; lotId: string }
  | { kind: "bin"; bin: string }
  | { kind: "set"; setName: string };

const PRODUCT_CATEGORY_MAP: Partial<Record<ProductType, StudioCategory>> = {
  "100-card Random Lot": "Random",
  "50-card Themed Lot": "Theme",
  "25-card Trainer Lot": "Trainer/Energy",
  "Holo/Reverse Bundle": "Theme",
  "Mystery Pack": "Random",
  "Starter/Kid Pack": "Random",
  "Set Builder Lot": "Set",
  "Character Theme Bundle": "Theme",
  "Type Theme Bundle": "Theme",
  "Pre-built Deck": "Decks",
  "Deck Core": "Decks",
  "Deck Upgrade Kit": "Decks",
  "Trainer Staples Kit": "Trainer/Energy",
  "Energy Kit": "Trainer/Energy",
};

function categoryFor(productType: ProductType): StudioCategory {
  return PRODUCT_CATEGORY_MAP[productType] ?? "All";
}

type IntentKey = Exclude<StudioCategory, "All">;

const INTENT_TILES: Array<{
  intent: IntentKey;
  Icon: typeof Shuffle;
  description: string;
}> = [
  {
    intent: "Random",
    Icon: Shuffle,
    description: "Bulk lots, mystery packs, value mixes",
  },
  {
    intent: "Set",
    Icon: Grid3x3,
    description: "Same-set lots and binder fillers",
  },
  { intent: "Theme", Icon: Tag, description: "Type, character, holo bundles" },
  {
    intent: "Decks",
    Icon: Layers,
    description: "Pre-built decks, cores, upgrade kits",
  },
  {
    intent: "Trainer/Energy",
    Icon: Zap,
    description: "Trainer staples & energy kits",
  },
];

function ProductStudioPage() {
  const navigate = useNavigate();
  const {
    cards,
    lots,
    storageBins,
    shippingSupplies,
    addProductWithPackaging,
    deckTemplates,
    bundleTemplates,
    bundleSettings,
  } = useApp();

  const [selectedIntent, setSelectedIntent] = useState<IntentKey | null>(null);
  const [source, setSource] = useState<InventorySource>({
    kind: "all-unlisted",
  });

  const sourceCards = useMemo<Card[]>(() => {
    const unlisted = cards.filter(
      (c) => c.listingStatus === "Unlisted" && c.quantity > 0,
    );
    if (source.kind === "all-unlisted") return unlisted;
    if (source.kind === "lot")
      return unlisted.filter((c) => c.sourceLotId === source.lotId);
    if (source.kind === "bin")
      return unlisted.filter((c) => c.storageLocation === source.bin);
    if (source.kind === "set")
      return unlisted.filter((c) => c.setName === source.setName);
    return unlisted;
  }, [cards, source]);

  const opportunities = useMemo<ProductOpportunity[]>(() => {
    if (sourceCards.length === 0) return [];
    const builtin = generateBundles(sourceCards, bundleSettings, {
      includePartial: true,
    });
    // Run each template independently (fresh empty `claimed` set per template)
    // so suggestions reflect "what could you make from this inventory" rather
    // than "what's left after earlier templates claimed cards." Cards only
    // become committed when the user actually creates a draft product, not
    // during suggestion. Matches the bundle-templates manager's per-template
    // availability so the two views never disagree.
    const userBundles = bundleTemplates.flatMap((t) =>
      generateTemplateBundles(sourceCards, [t], new Set<string>(), {
        mixOverrides: bundleSettings.mixOverrides,
      }),
    );
    return generateProductOpportunities({
      cards: sourceCards,
      bundleSuggestions: [...builtin, ...userBundles],
      supplies: shippingSupplies,
    });
  }, [sourceCards, bundleTemplates, shippingSupplies, bundleSettings]);

  const intentCounts = useMemo(() => {
    const out: Record<IntentKey, { ready: number; close: number }> = {
      Random: { ready: 0, close: 0 },
      Set: { ready: 0, close: 0 },
      Theme: { ready: 0, close: 0 },
      Decks: { ready: 0, close: 0 },
      "Trainer/Energy": { ready: 0, close: 0 },
    };
    for (const opp of opportunities) {
      const cat = categoryFor(opp.productType);
      if (cat === "All") continue;
      if (opp.availabilityPct >= 100) out[cat as IntentKey].ready++;
      else if (opp.availabilityPct >= 80) out[cat as IntentKey].close++;
    }
    return out;
  }, [opportunities]);

  const intentOpps = useMemo<ProductOpportunity[]>(() => {
    if (!selectedIntent) return [];
    return opportunities
      .filter((o) => categoryFor(o.productType) === selectedIntent)
      .sort(
        (a, b) =>
          b.availabilityPct - a.availabilityPct ||
          b.expectedProfit - a.expectedProfit,
      );
  }, [opportunities, selectedIntent]);

  const allSets = useMemo(() => {
    const seen = new Set<string>();
    for (const c of cards) {
      if (c.listingStatus === "Unlisted" && c.setName) seen.add(c.setName);
    }
    return [...seen].sort();
  }, [cards]);

  const handleSave = (opportunity: ProductOpportunity) => {
    const productId = generateId("PROD");
    const description = (() => {
      try {
        return buildDescriptionForProduct(
          {
            productType: opportunity.productType,
            bundleStrategyId: opportunity.strategyId,
            targetSalePrice: opportunity.expectedSalePrice,
          },
          opportunity.cards,
        ).plainText;
      } catch {
        return opportunity.description;
      }
    })();
    addProductWithPackaging({
      id: productId,
      productType: opportunity.productType,
      name: opportunity.title,
      quantity: 1,
      cardCount: opportunity.cardCount,
      cardIds: opportunity.cardIds,
      sourceLotId: "",
      costBasis: opportunity.estimatedCostBasis,
      packagingCost: 0,
      targetSalePrice: opportunity.expectedSalePrice,
      listingTitle: opportunity.title,
      description,
      storageLocation: "",
      status: "Draft",
      createdAt: new Date().toISOString(),
      bundleStrategyId: opportunity.strategyId,
    });
    navigate({ to: "/products/$id", params: { id: productId } });
  };

  // ── Stage 2: intent drill-down ───────────────────────────────────────────
  if (selectedIntent) {
    return (
      <section className="flex h-full flex-col">
        <div className="border-b border-border pb-3">
          <button
            type="button"
            onClick={() => setSelectedIntent(null)}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" /> Back to intents
          </button>
          <h1 className="mt-2 text-2xl font-bold">{selectedIntent}</h1>
          <p className="text-sm text-muted-foreground">
            {intentOpps.length === 0
              ? "No bundles available with the current inventory source."
              : `${intentCounts[selectedIntent].ready} ready · ${intentCounts[selectedIntent].close} nearly ready`}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto py-4">
          {intentOpps.length === 0 ? (
            <EmptyState
              icon={<Filter className="h-8 w-8" />}
              title="No matches yet"
              description="Try a different intent, broaden the inventory source, or add bundle templates."
            />
          ) : (
            <div className="space-y-3">
              {intentOpps.map((opp) => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  onSave={handleSave}
                />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  // ── Stage 1: intent grid ─────────────────────────────────────────────────
  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/products"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Close
        </Link>
        <h1 className="mt-2 text-2xl font-bold">Product Lot Studio</h1>
        <p className="text-sm text-muted-foreground">
          Pick a build intent to see how many bundles your inventory can fulfill
          — fully or nearly.
        </p>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto py-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {INTENT_TILES.map(({ intent, Icon, description }) => {
            const counts = intentCounts[intent];
            const total = counts.ready + counts.close;
            const disabled = sourceCards.length === 0 || total === 0;
            return (
              <button
                key={intent}
                type="button"
                onClick={() => {
                  if (disabled) return;
                  setSelectedIntent(intent);
                }}
                disabled={disabled}
                className={`flex min-w-0 flex-col gap-2 rounded-md border border-border bg-card p-3 text-left transition-opacity ${
                  disabled ? "opacity-55" : "hover:bg-card/80"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span className="flex h-7 w-7 items-center justify-center rounded-md bg-muted">
                    <Icon className="h-4 w-4 text-primary" />
                  </span>
                  <span className="text-sm font-semibold text-foreground">
                    {intent}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{description}</p>
                <div className="flex items-center gap-3">
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                    <CheckCircle className="h-3 w-3" />
                    {counts.ready} ready
                  </span>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-500">
                    <Circle className="h-3 w-3" />
                    {counts.close} close
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        <div className="space-y-1.5">
          <label
            htmlFor="studio-source-select"
            className="block text-[10px] font-medium tracking-wider text-muted-foreground"
          >
            INVENTORY SOURCE
          </label>
          <select
            id="studio-source-select"
            value={
              source.kind === "all-unlisted"
                ? "all"
                : source.kind === "lot"
                  ? `lot:${source.lotId}`
                  : source.kind === "bin"
                    ? `bin:${source.bin}`
                    : `set:${source.setName}`
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === "all") setSource({ kind: "all-unlisted" });
              else if (v.startsWith("lot:"))
                setSource({ kind: "lot", lotId: v.slice(4) });
              else if (v.startsWith("bin:"))
                setSource({ kind: "bin", bin: v.slice(4) });
              else if (v.startsWith("set:"))
                setSource({ kind: "set", setName: v.slice(4) });
            }}
            className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground focus:border-primary focus:outline-none"
          >
            <option value="all">All unlisted</option>
            {lots.length > 0 && (
              <optgroup label="By lot">
                {lots.map((lot) => (
                  <option key={lot.id} value={`lot:${lot.id}`}>
                    {lot.seller || lot.id}
                  </option>
                ))}
              </optgroup>
            )}
            {storageBins.length > 0 && (
              <optgroup label="By bin">
                {storageBins.map((bin) => (
                  <option key={bin.id} value={`bin:${bin.name}`}>
                    {bin.name}
                  </option>
                ))}
              </optgroup>
            )}
            {allSets.length > 0 && (
              <optgroup label="By set">
                {allSets.map((setName) => (
                  <option key={setName} value={`set:${setName}`}>
                    {setName}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
              SAVED DECKS
            </div>
            {deckTemplates.length > 0 && (
              <Link
                to="/tools/deck-templates"
                className="text-xs font-medium text-secondary hover:underline"
              >
                Manage
              </Link>
            )}
          </div>
          {deckTemplates.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No saved decks yet — paste a deck list and tap Save to add one.
            </p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {deckTemplates.map((t) => (
                <Link
                  key={t.id}
                  to="/products/deck-import"
                  className="min-w-0 max-w-[12rem] rounded-md border border-border bg-card p-2 text-left hover:bg-card/80"
                >
                  <div className="truncate text-xs font-semibold text-foreground">
                    {t.name}
                  </div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    {t.format} · {t.intent}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-medium tracking-wider text-muted-foreground">
              BUNDLE TEMPLATES
            </div>
            <Link
              to="/tools/bundle-templates"
              className="text-xs font-medium text-secondary hover:underline"
            >
              {bundleTemplates.length > 0 ? "Manage" : "Add"}
            </Link>
          </div>
          <p className="text-xs text-muted-foreground">
            {bundleTemplates.length === 0
              ? "No bundle templates yet — define recipes (set/rarity/type filters) so the studio matches them automatically."
              : `${bundleTemplates.length} template${
                  bundleTemplates.length === 1 ? "" : "s"
                } active — matches appear in the intent buckets above.`}
          </p>
        </div>

        <Link
          to="/products/deck-import"
          className="flex items-center gap-3 rounded-md border border-border bg-card p-3 hover:bg-card/80"
        >
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-foreground">
              Build a deck from a TCGplayer-style list
            </div>
            <div className="text-xs text-muted-foreground">
              Paste a deck list, match it against your inventory, and create a
              Pre-built Deck, Deck Core, or Upgrade Kit.
            </div>
          </div>
          <div className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">
            Import deck
            <ChevronRight className="h-3 w-3" />
          </div>
        </Link>

        {sourceCards.length === 0 && (
          <EmptyState
            icon={<Inbox className="h-8 w-8" />}
            title="No unlisted inventory"
            description="Add cards to your inventory or pick a different source filter to see product opportunities."
          />
        )}
      </div>
    </section>
  );
}

function OpportunityCard({
  opportunity,
  onSave,
}: {
  opportunity: ProductOpportunity;
  onSave: (o: ProductOpportunity) => void;
}) {
  const isProfitable = opportunity.expectedProfit >= 0;
  const rarityCounts = summarizeRarityCounts(opportunity.cards);
  const isFullyAvailable = opportunity.availabilityPct >= 100;
  const availabilityColor = isFullyAvailable
    ? "text-emerald-600"
    : "text-amber-500";
  const availabilityBg = isFullyAvailable ? "bg-emerald-500" : "bg-amber-500";

  return (
    <button
      type="button"
      onClick={() => onSave(opportunity)}
      className="block w-full rounded-md border border-border bg-card p-4 text-left transition-colors hover:bg-card/80"
    >
      <div className="space-y-1">
        <div className="text-sm font-semibold text-foreground line-clamp-2">
          {opportunity.title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <Badge label={opportunity.productType} variant="default" />
          <span className="text-xs text-muted-foreground">
            {opportunity.cardCount} cards
          </span>
        </div>
      </div>

      <div className="mt-3 space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {isFullyAvailable ? "Ready to build" : "Nearly ready"}
          </span>
          <span className={`text-xs font-semibold ${availabilityColor}`}>
            {opportunity.availabilityPct}% · {opportunity.cardCount} /{" "}
            {opportunity.targetCount}
          </span>
        </div>
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${availabilityBg}`}
            style={{
              width: `${Math.max(8, Math.min(100, opportunity.availabilityPct))}%`,
            }}
          />
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1">
        {RARITY_DISPLAY_ORDER.filter((code) => rarityCounts[code] > 0).map(
          (code) => (
            <span
              key={code}
              className="inline-flex items-center gap-1 rounded-md bg-muted px-1.5 py-0.5 text-[11px]"
            >
              <span className="font-semibold text-foreground">{code}</span>
              <span className="text-muted-foreground">
                {rarityCounts[code]}
              </span>
            </span>
          ),
        )}
      </div>

      <div className="mt-3 grid grid-cols-3 gap-2">
        <StatCell
          label="Buyer value"
          value={`${opportunity.buyerValueScore}/100`}
          color="text-secondary"
        />
        <StatCell
          label="Confidence"
          value={`${opportunity.confidenceScore}/100`}
          color="text-secondary"
        />
        <StatCell
          label="Listing"
          value={
            opportunity.recommendedListingType === "Auction"
              ? "Auction"
              : "Fixed"
          }
          color={
            opportunity.recommendedListingType === "Auction"
              ? "text-amber-500"
              : "text-emerald-600"
          }
        />
      </div>

      <div className="mt-3">
        <ProductPricingSummary
          estimatedMarketValue={opportunity.estimatedMarketValue}
          estimatedCostBasis={opportunity.estimatedCostBasis}
          expectedSalePrice={opportunity.expectedSalePrice}
          expectedProfit={opportunity.expectedProfit}
          expectedMarginPct={opportunity.expectedMarginPct}
          fulfillment={opportunity.estimatedFulfillmentCost}
        />
      </div>

      {opportunity.reasons.length > 0 && (
        <div className="mt-3 space-y-1">
          {opportunity.reasons.slice(0, 2).map((reason, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <Check className="mt-0.5 h-3 w-3 shrink-0 text-emerald-600" />
              <p className="text-xs text-muted-foreground">{reason}</p>
            </div>
          ))}
        </div>
      )}

      {opportunity.warnings.length > 0 && (
        <div className="mt-2 space-y-1">
          {opportunity.warnings.slice(0, 2).map((warning, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <span className="mt-0.5 h-3 w-3 shrink-0 rounded-full bg-amber-500" />
              <p className="text-xs text-amber-600">{warning}</p>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3 flex items-center justify-end gap-1 text-sm font-semibold text-secondary">
        {isProfitable ? "Review & save" : "Review (low margin)"}
        <ChevronRight className="h-4 w-4" />
      </div>
    </button>
  );
}

function StatCell({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center gap-0.5">
      <div className={`text-sm font-semibold ${color}`}>{value}</div>
      <div className="text-[10px] text-muted-foreground">{label}</div>
    </div>
  );
}
